import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Timeline,
} from "@mui/icons-material";
import marketIndexService, {
  MarketIndexData,
  MarketIndexMinuteData,
} from "../../services/marketIndexService";

interface MarketIndexChartProps {
  height?: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // 毫秒
}

// 自定義 Tooltip — 定義在模組層級,避免每次 render 重建導致 tooltip 重掛
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          時間：{label}
        </Typography>
        <Typography variant="body2" color="primary">
          指數：{marketIndexService.formatNumber(data.price)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          成交量：{data.volume?.toLocaleString()}
        </Typography>
      </Box>
    );
  }
  return null;
};

const MarketIndexChart: React.FC<MarketIndexChartProps> = ({
  height = 700,
  autoRefresh = true,
  refreshInterval = 30000, // 30秒更新一次
}) => {
  const [currentTab, setCurrentTab] = useState(0); // 0: 加權指數, 1: 櫃買指數
  const [tseData, setTseData] = useState<MarketIndexData | null>(null);
  const [otcData, setOtcData] = useState<MarketIndexData | null>(null);
  const [tseMinuteData, setTseMinuteData] = useState<MarketIndexMinuteData[]>(
    []
  );
  const [otcMinuteData, setOtcMinuteData] = useState<MarketIndexMinuteData[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // UI-03:開盤狀態改為每次 tick 動態檢查 —
  // 舊版 useMemo([]) 只在 mount 時取樣,盤前掛載整日不刷新、盤中掛載收盤後永遠續打
  const [isMarketOpen, setIsMarketOpen] = useState(() => marketIndexService.isMarketOpen());

  // 獲取指數資料 - 優化版，只調用 2 次 API 而非 4 次
  const fetchData = async (isMountedRef?: React.MutableRefObject<boolean>) => {
    try {
      setError(null);

      // 使用新的優化 API，只調用 2 次而非 4 次
      const allData = await marketIndexService.getAllData();

      // 檢查組件是否仍然掛載才更新 state
      if (!isMountedRef || isMountedRef.current) {
        setTseData(allData.tse.index);
        setOtcData(allData.otc.index);
        setTseMinuteData(allData.tse.minuteData);
        setOtcMinuteData(allData.otc.minuteData);
      }
    } catch (err) {
      if (!isMountedRef || isMountedRef.current) {
        setError("載入市場指數資料失敗");
      }
    } finally {
      if (!isMountedRef || isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // 初始載入和自動更新
  useEffect(() => {
    let isMounted = true;
    const isMountedRef = { current: true };

    const fetchDataSafely = async () => {
      if (isMounted) {
        await fetchData(isMountedRef);
      }
    };

    // 初始載入
    fetchDataSafely();

    // 設置自動更新:interval 常駐,於每次 tick 內檢查交易時段
    // (比照 Holdings 的做法 — 開盤/收盤跨越時才會正確啟停)
    let interval: ReturnType<typeof setInterval> | null = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        const open = marketIndexService.isMarketOpen();
        setIsMarketOpen(open);
        if (open) {
          fetchDataSafely();
        }
      }, refreshInterval);
    }

    // 清理函數
    return () => {
      isMounted = false;
      isMountedRef.current = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, refreshInterval]);

  // 當前顯示的資料
  const currentData = currentTab === 0 ? tseData : otcData;
  const currentMinuteData = currentTab === 0 ? tseMinuteData : otcMinuteData;

  // 圖表資料處理
  const chartData = useMemo(() => {
    // 生成固定的時間範圍 9:00-13:30 (每5分鐘一個點)
    const generateTimeRange = () => {
      const times = [];
      const startHour = 9;
      const startMinute = 0;
      const endHour = 13;
      const endMinute = 30;
      
      for (let hour = startHour; hour <= endHour; hour++) {
        let maxMinute = hour === endHour ? endMinute : 55;
        for (let minute = hour === startHour ? startMinute : 0; minute <= maxMinute; minute += 5) {
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          times.push(timeStr);
        }
      }
      return times;
    };
    
    const allTimes = generateTimeRange();
    
    // 如果沒有實際數據，返回空的時間範圍結構
    if (!currentMinuteData.length) {
      return allTimes.map(time => ({
        time,
        price: null,
        volume: 0,
      }));
    }

    // 建立實際數據的映射
    const dataMap = new Map();
    currentMinuteData.forEach(item => {
      dataMap.set(item.time, {
        price: item.price,
        volume: item.volume,
      });
    });

    // 生成完整的圖表數據，填補缺失的時間點
    return allTimes.map(time => {
      const data = dataMap.get(time);
      return {
        time,
        price: data ? data.price : null,
        volume: data ? data.volume : 0,
      };
    });
  }, [currentMinuteData]);

  // 顏色主題
  const getThemeColors = (change: number) => {
    if (change > 0) {
      return {
        primary: "#d32f2f", // 紅色（台股上漲用紅色）
        bg: "#ffebee",
        border: "#f44336",
      };
    } else if (change < 0) {
      return {
        primary: "#2e7d32", // 綠色（台股下跌用綠色）
        bg: "#e8f5e8",
        border: "#4caf50",
      };
    }
    return {
      primary: "#616161", // 平盤灰色
      bg: "#f5f5f5",
      border: "#9e9e9e",
    };
  };

  if (loading) {
    return (
      <Card
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            載入市場指數資料中...
          </Typography>
        </Box>
      </Card>
    );
  }

  if (error || !currentData) {
    return (
      <Card sx={{ height }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "無法載入市場指數資料"}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const themeColors = getThemeColors(currentData.change);

  return (
    <Card sx={{ height, borderRadius: 2, boxShadow: 3 }}>
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 頂部選項卡 - 包含指數名稱 */}
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{
            minHeight: 52,
            mb: 3,
            "& .MuiTab-root": {
              minHeight: 52,
              fontSize: "1rem",
              fontWeight: "bold",
              textTransform: "none",
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={tseData ? `${tseData.name} (TAIEX)` : "台灣加權指數"}
            icon={<Timeline />}
            iconPosition="start"
          />
          <Tab
            label={otcData ? `${otcData.name} (OTC)` : "櫃買指數"}
            icon={<Timeline />}
            iconPosition="start"
          />
        </Tabs>

        {/* 第一排：主要價格信息和狀態 - RWD 優化 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            gap: { xs: 2, md: 0 },
          }}
        >
          {/* 左側：當前指數 與 漲跌幅 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 3 },
              flex: { xs: 1, md: "auto" },
            }}
          >
            {/* 當前指數 */}
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                當前指數
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: themeColors.primary,
                  lineHeight: 1,
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "2rem" },
                }}
              >
                {marketIndexService.formatNumber(currentData.current)}
              </Typography>
            </Box>

            {/* 漲跌幅 */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                漲跌幅
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  icon={
                    currentData.change >= 0 ? <TrendingUp /> : <TrendingDown />
                  }
                  label={marketIndexService.formatChange(
                    currentData.change,
                    currentData.changePercent
                  )}
                  sx={{
                    color: "white",
                    bgcolor: themeColors.primary,
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", md: "0.9rem" },
                    height: { xs: 30, md: 36 },
                    "& .MuiChip-icon": { fontSize: { xs: 14, md: 18 } },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 右側：市場狀態和更新時間 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-end", md: "flex-end" },
              gap: { xs: 1, md: 1.5 },
              minWidth: { xs: "auto", md: "auto" },
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            {/* 市場狀態 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {!isMarketOpen && (
                <Chip
                  label="休市"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{
                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                    fontWeight: "bold",
                    borderWidth: "2px",
                    height: { xs: 24, md: 28 },
                    "&:hover": {
                      borderWidth: "2px",
                    },
                  }}
                />
              )}
              {isMarketOpen && (
                <Chip
                  label="交易中"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{
                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                    fontWeight: "bold",
                    height: { xs: 24, md: 28 },
                    boxShadow: "0 2px 4px rgba(76, 175, 80, 0.3)",
                  }}
                />
              )}
            </Box>

            {/* 更新時間 */}
            <Box
              sx={{
                textAlign: "right",
                background: "rgba(0,0,0,0.02)",
                borderRadius: 2,
                p: { xs: 0.8, md: 1.5 },
                border: "1px solid rgba(0,0,0,0.08)",
                minWidth: { xs: "90px", md: "120px" },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                  fontWeight: "500",
                  letterSpacing: "0.3px",
                  textTransform: "uppercase",
                  display: "block",
                }}
              >
                更新
              </Typography>
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.9rem" },
                  color: "text.primary",
                  mt: { xs: 0.1, md: 0.5 },
                  fontFamily: "monospace",
                  lineHeight: 1.1,
                }}
              >
                {currentData.updateTime}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 關鍵數據 - 現代化設計 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "1fr 1fr 1fr",
              md: "1fr 1fr 1fr 1fr",
              lg: "1fr 1fr 1fr 1fr 1fr",
            },
            gap: { xs: 1.5, sm: 2, md: 1.5 },
            mb: 3,
            p: { xs: 1, sm: 1.5 },
            background:
              "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(241,245,249,0.9) 100%)",
            borderRadius: 3,
            border: "1px solid rgba(226,232,240,0.6)",
          }}
        >
          {/* 開盤價 */}
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 1.5, md: 1 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.8)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                color: "text.secondary",
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              開盤
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: "700",
                color: "#1565c0",
                lineHeight: 1.2,
              }}
            >
              {marketIndexService.formatNumber(currentData.open)}
            </Typography>
          </Box>

          {/* 最高價 */}
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 1.5, md: 1 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.8)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                color: "text.secondary",
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              最高
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: "700",
                color: "#d32f2f",
                lineHeight: 1.2,
              }}
            >
              {marketIndexService.formatNumber(currentData.high)}
            </Typography>
          </Box>

          {/* 最低價 */}
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 1.5, md: 1 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.8)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                color: "text.secondary",
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              最低
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: "700",
                color: "#2e7d32",
                lineHeight: 1.2,
              }}
            >
              {marketIndexService.formatNumber(currentData.low)}
            </Typography>
          </Box>

          {/* 成交量 */}
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 1.5, md: 1 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.8)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                color: "text.secondary",
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              成交量
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: "700",
                color: "#00695c",
                lineHeight: 1.2,
              }}
            >
              {currentData.volume.toLocaleString()}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "text.secondary",
                display: "block",
                mt: 0.2,
              }}
            >
              億元
            </Typography>
          </Box>

          {/* 昨收價 - 新增 */}
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 1.5, md: 1 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              display: { xs: "none", sm: "none", md: "block", lg: "block" },
              "&:hover": {
                background: "rgba(255,255,255,0.8)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                color: "text.secondary",
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              昨收
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: "700",
                color: "#616161",
                lineHeight: 1.2,
              }}
            >
              {marketIndexService.formatNumber(currentData.previousClose)}
            </Typography>
          </Box>
        </Box>

        {/* 走勢圖 - 確保完整顯示 */}
        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 250, md: 300 },
            width: "100%",
            overflow: "hidden",
          }}
        >
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  fontSize={11}
                  interval={Math.floor(chartData.length / 8)} // 顯示約8個時間點
                  tick={{ fill: "#666" }}
                  tickMargin={8}
                  domain={['09:00', '13:30']}
                  type="category"
                />
                <YAxis
                  fontSize={12}
                  domain={["dataMin - 10", "dataMax + 10"]}
                  tick={{ fill: "#666" }}
                  tickFormatter={(value) => value.toFixed(0)}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  y={currentData.previousClose}
                  stroke="#999"
                  strokeDasharray="2 2"
                  label={{ value: "昨收", position: "top" }}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={themeColors.primary}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    stroke: themeColors.primary,
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">暫無分鐘線資料</Typography>
            </Box>
          )}
        </Box>

      </CardContent>
    </Card>
  );
};

export default MarketIndexChart;
